/**
 * Data-loading hook for the unified Tests page.
 *
 * After #596 retired the server-side merged `/api/test-cases` endpoint,
 * this hook fans out three parallel fetches and merges the script-side
 * view on the client:
 *
 *   - `testPlansAPI.getTestCases` → `PlanTestCase[]` (English content:
 *     name, description, mcpSteps, priority/intent/role, tags,
 *     fixture deps, snapshots, blocked reason).
 *   - `testScriptsAPI.list` → `TestScriptSummary[]` (script code +
 *     health timestamps). Merged into the per-case view via
 *     `mergeTestCases`, indexed by case id for cheap lookup.
 *   - `testSuitesAPI.getByAppAndVariant` → `TestSuite[]` with
 *     ordered `testCaseIds`.
 *
 * Plan cases without a script still surface — `mergeTestCases` emits
 * an empty `scriptCode` + missing health fields so the detail pane
 * can render the planned-but-not-generated state.
 *
 * Hand-rolled `useEffect+useState` matches the surrounding
 * test-family pages (TestPlan, TestSuites). React Query is reserved
 * for the dashboard subtree per existing convention; introducing it
 * here would be scope creep.
 *
 * Returns a `reload` so save handlers in the detail panes can
 * trigger a refetch without redefining the load logic.
 */
import { useCallback, useEffect, useState } from 'react'
import { testPlansAPI, testScriptsAPI, testSuitesAPI } from '@/lib/api'
import { useAppContext } from '@/hooks/useAppContext'
import type { PlanTestCase, TestCase, TestSuite } from '@/types'
import { mergeTestCases } from '@/types'
import type { TestsData } from './types'

export function useTestsData(): TestsData & { reload: () => Promise<void> } {
  const { selectedAppId, selectedVariantId } = useAppContext()
  const [cases, setCases] = useState<PlanTestCase[]>([])
  const [suites, setSuites] = useState<TestSuite[]>([])
  const [scriptByCaseId, setScriptByCaseId] = useState<Record<string, TestCase>>({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const reload = useCallback(async () => {
    if (!selectedAppId || !selectedVariantId) {
      setCases([])
      setSuites([])
      setScriptByCaseId({})
      setLoading(false)
      return
    }
    try {
      const [planCases, scripts, suiteList] = await Promise.all([
        testPlansAPI.getTestCases(selectedAppId, selectedVariantId),
        testScriptsAPI.list({ applicationId: selectedAppId, variantId: selectedVariantId }).catch(() => []),
        testSuitesAPI.getByAppAndVariant(selectedAppId, selectedVariantId),
      ])
      const merged = mergeTestCases(planCases, scripts)
      const scriptMap: Record<string, TestCase> = {}
      for (const m of merged) {
        if (m.id) scriptMap[m.id] = m
      }
      setCases(planCases)
      setSuites(suiteList)
      setScriptByCaseId(scriptMap)
      setError(null)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load tests')
    } finally {
      setLoading(false)
    }
  }, [selectedAppId, selectedVariantId])

  useEffect(() => {
    setLoading(true)
    void reload()
  }, [reload])

  return { cases, suites, scriptByCaseId, loading, error, reload }
}
